// The electrode sites over the picture (spec §15, `component.electrode-site`): the run's 10–20 scalp
// sites and its depth contacts, projected with the camera as the HUD's other marks are and the same
// size at every zoom, under the callouts. A site is ivory over quiet cortex. As the activity under it
// rises from 0.3 to 0.6, the disc mixes to the ictal amber and a 24 px amber halo fades in, added to
// the picture. A scalp site well round the far side of the head is left out, so a side view shows
// the near side's sites; depth contacts are always drawn.
// On a real patient (the HUP fits) the sites are their own implanted contacts instead, lit by their own
// recording (RecordedLevels), and those the clinicians marked in the seizure onset zone are ringed.
// A click on a site selects it (`electrode:<kind>:<channel>`) for the inspector, which rings it.

import { useEffect, useRef, type MouseEvent } from 'react';
import { on } from '../viewport/events.ts';
import { useAtlas, type AtlasState } from '../app/store.ts';
import { openedSlot } from '../app/sims.ts';
import { defaultSource } from '../data/sims/files.ts';
import { isPatientComparison, openComparison } from '../data/sims/comparison.ts';
import type { DynamicsSim } from '../data/sims/dynamics.ts';
import { patientSites, RecordedLevels, sensorSites, siteId, siteLevels, type Levels, type SensorKind, type SiteKind, type Vec3 } from '../data/sims/electrodes.ts';
import type { Rect } from './callouts.ts';
import type { ViewportEvent } from '../viewport/api.ts';

type CameraEvent = Extract<ViewportEvent, { ev: 'camera' }>;

/** The sites the callouts keep clear of, each disc grown by its clearance; Labels reads them each solve. */
export const siteRects: { rects: Rect[] } = { rects: [] };

/** Where the far-side test looks from, RAS mm: about the head's centre. */
const HEAD: Vec3 = [0, -17, 15];
/** A scalp site further round the far side than this (cosine from the eye's side) is left out. */
const FAR = -0.35;
export type { SiteKind };
const KINDS: SiteKind[] = ['eeg', 'seeg', 'contacts'];

interface KindSites {
  levels: Levels | null;
  names: string[];
  /** By site: in the clinicians' onset zone (a patient's contacts). */
  soz?: boolean[];
}

/** A run's sites by kind: a patient's own contacts, else its forward model's sensors. */
async function loadSites(path: string, sim: DynamicsSim): Promise<Partial<Record<SiteKind, KindSites>>> {
  if (isPatientComparison(sim.header)) {
    const c = await openComparison(sim).catch(() => null);
    const sites = c ? patientSites(c) : null;
    return c && sites ? { contacts: { levels: new RecordedLevels(c), names: sites.names, soz: sites.soz } } : {};
  }
  const sites = await sensorSites(defaultSource(), path, sim).catch(() => ({}));
  const kinds: Partial<Record<SiteKind, KindSites>> = {};
  for (const k of ['eeg', 'seeg'] as const) {
    const st = (sites as Partial<Record<SensorKind, { names: string[]; positions: Vec3[] }>>)[k];
    if (st) kinds[k] = { levels: await siteLevels(sim, st).catch(() => null), names: st.names };
  }
  return kinds;
}

function project(m: Float32Array, p: Vec3, w: number, h: number): [number, number] | null {
  const cx = m[0]! * p[0] + m[4]! * p[1] + m[8]! * p[2] + m[12]!;
  const cy = m[1]! * p[0] + m[5]! * p[1] + m[9]! * p[2] + m[13]!;
  const cw = m[3]! * p[0] + m[7]! * p[1] + m[11]! * p[2] + m[15]!;
  if (cw <= 1e-6) return null;
  return [((cx / cw + 1) / 2) * w, ((1 - cy / cw) / 2) * h];
}

function facing(p: Vec3, eye: Vec3): number {
  const a = [p[0] - HEAD[0], p[1] - HEAD[1], p[2] - HEAD[2]];
  const b = [eye[0] - HEAD[0], eye[1] - HEAD[1], eye[2] - HEAD[2]];
  const n = Math.hypot(a[0]!, a[1]!, a[2]!) * Math.hypot(b[0]!, b[1]!, b[2]!);
  return n > 0 ? (a[0]! * b[0]! + a[1]! * b[1]! + a[2]! * b[2]!) / n : 1;
}

const cssNumber = (name: string, fallback: number) => {
  if (typeof document === 'undefined') return fallback;
  const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
  return Number.isFinite(v) ? v : fallback;
};

/** The main slot's dynamics run, while the scene shows it. */
export const mainRun = (s: AtlasState) => (s.sims.scenario === s.scenario ? s.sims.slots.find((x) => x.slot === 'main' && x.format === 'dynamics' && !x.appOnly) : undefined);

export function ElectrodeSites() {
  const halos = useRef<HTMLDivElement>(null);
  const discs = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let camera: CameraEvent | null = null;
    let loaded: { path: string; kinds: Partial<Record<SiteKind, KindSites>> } | null = null;
    let want: string | null = null;
    let raf = 0;
    const lo = cssNumber('--ba-component-electrode-site-rest-below', 0.3);
    const hi = cssNumber('--ba-component-electrode-site-active-above', 0.6);
    const dot = cssNumber('--ba-component-electrode-site-dot', 8);
    const clearance = cssNumber('--ba-component-electrode-site-clearance', 2);

    const draw = () => {
      raf = 0;
      const a = halos.current;
      const b = discs.current;
      if (!a || !b) return;
      const s = useAtlas.getState();
      const shown = loaded && camera && loaded.path === mainRun(s)?.path ? KINDS.filter((k) => s.toggles[k] && loaded!.kinds[k]?.levels) : [];
      const w = b.clientWidth;
      const h = b.clientHeight;
      const rects: Rect[] = [];
      let n = 0;
      for (const kind of shown) {
        const { levels, names, soz } = loaded!.kinds[kind]!;
        const level = levels!.at(s.time);
        levels!.sites.forEach((p, i) => {
          if (kind === 'eeg' && facing(p, camera!.eyeMm as Vec3) < FAR) return;
          const xy = project(camera!.viewProj, p, w, h);
          if (!xy || xy[0] < -24 || xy[1] < -24 || xy[0] > w + 24 || xy[1] > h + 24) return;
          const f = Math.min(1, Math.max(0, (level[i]! - lo) / (hi - lo)));
          let halo = a.children[n] as HTMLSpanElement | undefined;
          let disc = b.children[n] as HTMLSpanElement | undefined;
          if (!halo) halo = a.appendChild(document.createElement('span'));
          if (!disc) disc = b.appendChild(document.createElement('span'));
          const t = `translate3d(${xy[0].toFixed(1)}px, ${xy[1].toFixed(1)}px, 0)`;
          halo.style.transform = t;
          disc.style.transform = t;
          halo.style.setProperty('--f', f.toFixed(3));
          disc.style.setProperty('--f', f.toFixed(3));
          disc.dataset.site = `${kind}:${names[i]}`;
          if (soz?.[i]) disc.dataset.soz = '';
          else delete disc.dataset.soz;
          if (s.selected === siteId(kind, names[i]!)) disc.dataset.selected = '';
          else delete disc.dataset.selected;
          rects.push({ x: xy[0] - dot / 2 - clearance, y: xy[1] - dot / 2 - clearance, w: dot + 2 * clearance, h: dot + 2 * clearance });
          n++;
        });
      }
      while (a.children.length > n) a.lastChild!.remove();
      while (b.children.length > n) b.lastChild!.remove();
      siteRects.rects = rects;
    };
    const later = () => {
      if (!raf) raf = requestAnimationFrame(draw);
    };

    const load = () => {
      const s = useAtlas.getState();
      const run = mainRun(s);
      const o = run ? openedSlot('main') : undefined;
      if (!run || o?.format !== 'dynamics') {
        want = null;
        later();
        return;
      }
      if (want === run.path) return;
      want = run.path;
      const path = run.path;
      void (async () => {
        const kinds = await loadSites(path, o.sim);
        if (want !== path) return;
        loaded = { path, kinds };
        later();
      })();
    };

    const offCamera = on('camera', (e) => {
      camera = e;
      later();
    });
    const unsub = useAtlas.subscribe(
      (s) => [s.sims, s.scenario, s.toggles, s.time, s.selected] as const,
      () => {
        load();
        later();
      },
      { equalityFn: (x, y) => x.every((v, i) => v === y[i]) },
    );
    load();
    return () => {
      offCamera();
      unsub();
      cancelAnimationFrame(raf);
      siteRects.rects = [];
    };
  }, []);
  // The discs take the pointer (the layer between them doesn't), so a click on one selects it.
  const pick = (e: MouseEvent) => {
    const site = (e.target as HTMLElement).closest<HTMLElement>('span[data-site]')?.dataset.site;
    if (site) useAtlas.getState().select(`electrode:${site}`);
  };
  return (
    <>
      <div ref={halos} className="site-halos" aria-hidden="true" />
      <div ref={discs} className="sites" aria-hidden="true" onClick={pick} />
    </>
  );
}

/** Which electrode kinds the scene's run has sites for: what the Electrodes section lists. */
export async function siteKinds(path: string): Promise<SiteKind[]> {
  const o = openedSlot('main');
  if (o?.format !== 'dynamics' || o.path !== path) return [];
  const kinds = await loadSites(path, o.sim);
  return KINDS.filter((k) => kinds[k]);
}
